import { useState } from 'react'
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query'
import { useIntuneQuery, intuneKeys } from '../hooks/useIntuneQuery'
import { useDeploymentJobsQuery, taskKeys } from '../hooks/useTaskList'
import { Link, useNavigate } from 'react-router-dom'
import { listDeployments, getDeploymentDetails, deleteDeployment, bulkRemoveDeployments, redeployToIntune, updateUninstallCommand, retryDeploymentJob, type Deployment, type DeploymentDetails, type DeploymentJob } from '../services/api'
import { ExclamationTriangleIcon, CheckCircleIcon, ArrowPathIcon, TrashIcon, Squares2X2Icon, EllipsisHorizontalIcon } from '@heroicons/react/24/outline'
import AssignmentModal from '../components/AssignmentModal'
import DeploymentJobsPanel from '../components/DeploymentJobsPanel'
import { GroupsNote, KeepUpdateOnlyOption, RelatedAppList, RelationshipNotes, RemovalPreviewStatus, RemoveScopeChoice, relatedSummary, removalBlocked, removalTarget, useRemovalPreview, type RemoveScope } from '../components/RemovalPreview'
import { PageHeader, Badge, EmptyState, Menu, MenuItem, SearchInput, SyncStatus, TableCard, TableSkeleton } from '../components/ui'
import { DevNote, useDevMode } from '../components/DevMode'
import { errorText } from '../utils/errorText'
import { plural } from '../utils/plural'
import { deploymentTitle, groupDeploymentRows } from '../utils/deploymentRows'
import DeploymentStatusBadge from '../components/DeploymentStatusBadge'
import { leftTheCatalog, notInCatalogLabel, notInCatalogPhrase } from '../utils/catalogEntry'

/** Which deployment run the reader has dismissed. Keyed by run id, so the next one still shows. */
const DISMISSED_RUN_KEY = 'deployments.dismissedRun'

const statusColors: Record<string, string> = {
  active: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400',
  pending: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400',
  failed: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400',
  unknown: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400',
  removed: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
}

const targetTypeLabels: Record<string, string> = {
  intune: 'Microsoft Intune',
  sccm: 'SCCM',
  appstore_client: 'Splashtop Agentic Packr client'
}

const sourceBadgeColors: Record<string, string> = {
  aeam_test: 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-400',
  ai_resolved: 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-400',
  manual: 'bg-yellow-100 dark:bg-yellow-900/50 text-yellow-700 dark:text-yellow-400',
  fallback: 'bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-400',
}

function UninstallCommandEditor({ deploymentId, commandline, source, onUpdate }: {
  deploymentId: string
  commandline?: string | null
  source?: string | null
  onUpdate: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(commandline || '')
  const [saving, setSaving] = useState(false)

  const handleSave = async () => {
    if (!value.trim()) return
    setSaving(true)
    try {
      await updateUninstallCommand(deploymentId, value.trim())
      setEditing(false)
      onUpdate()
    } catch (err) {
      console.error('Failed to update uninstall command:', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <label className="text-sm text-theme-text-tertiary">Intune Uninstall Command</label>
        {source && (
          <span className={`text-xs px-1.5 py-0.5 rounded ${sourceBadgeColors[source] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`}>
            {source === 'aeam_test' ? 'Tested' : source === 'ai_resolved' ? 'AI Resolved' : source === 'manual' ? 'Manual' : source === 'fallback' ? 'Fallback' : source}
          </span>
        )}
        {!editing && (
          <button
            onClick={() => { setValue(commandline || ''); setEditing(true) }}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            Edit
          </button>
        )}
      </div>

      {source === 'fallback' && !editing && (
        <div className="mb-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded text-xs text-red-700 dark:text-red-400">
          Uninstall may not work reliably. Consider editing the command manually.
        </div>
      )}

      {editing ? (
        <div className="flex gap-2">
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="flex-1 text-sm font-mono bg-white dark:bg-gray-800 border border-theme-border-primary rounded px-2 py-1.5 text-theme-text-primary"
            placeholder='e.g., msiexec.exe /X{GUID} /qn /norestart'
          />
          <button
            onClick={handleSave}
            disabled={saving || !value.trim()}
            className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={() => setEditing(false)}
            className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-theme-text-primary text-sm rounded"
          >
            Cancel
          </button>
        </div>
      ) : (
        <p className="text-theme-text-secondary text-sm font-mono bg-gray-100 dark:bg-gray-900 p-2 rounded overflow-x-auto">
          {commandline || <span className="text-theme-text-tertiary italic">Not set — using PSADT wrapper</span>}
        </p>
      )}
    </div>
  )
}

/** Every removal this page runs, single or bulk. */
const REMOVE_KEY = ['remove-deployments']

/** The catalog package a row belongs to; the Intune app name when the row has no package. */
const packageName = (d: Deployment) => d.package_name || d.app_name || 'Unknown App'

/** A template column entry: "<name> (deleted)" once the template is gone, "Deleted template" when
 *  the record predates recorded names. */
const templateLabel = (name: string | null | undefined, deleted?: boolean): string =>
  deleted ? (name ? `${name} (deleted)` : 'Deleted template') : name || '—'
const uxTemplateLabel = (d: Deployment) => templateLabel(d.ux_template_name, d.ux_template_deleted)
const deliveryTemplateLabel = (d: Deployment) =>
  templateLabel(d.delivery_template_name, d.delivery_template_deleted)

/** The pending version of a row, and when no automatic update can publish it, why, so the
 *  policy beside it is not read as the update being on its way. */
function PendingUpdate({ deployment }: { deployment: Deployment }) {
  if (!deployment.sync_pending && !deployment.update_state) return null
  const reason = deployment.update_reason
    ? deployment.update_reason.charAt(0).toUpperCase() + deployment.update_reason.slice(1)
    : null
  const to = deployment.update_to_version ? ` to v${deployment.update_to_version}` : ''
  if (deployment.update_state === 'cannot_rebuild' || deployment.update_state === 'objects_differ') {
    const text = `${reason ?? 'It cannot be updated automatically'}. Deploy it to update.`
    return (
      <div className="mt-0.5 max-w-[220px]" title={`Cannot update automatically${to}: ${text}`}>
        <p className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-600 dark:text-amber-400">
          <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" />
          Cannot update automatically
        </p>
        <p className="line-clamp-2 whitespace-normal text-[11px] text-fmuted">{text}</p>
      </div>
    )
  }
  if (deployment.update_state === 'failed') {
    return (
      <p
        className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-red-600 dark:text-red-400"
        title={`The automatic update${to} failed: ${deployment.update_reason ?? ''}`}
      >
        <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" />
        Update failed
      </p>
    )
  }
  if (deployment.update_state === 'up_to_date' || deployment.update_state === 'removal_pending') return null
  return (
    <p className="text-[11px] font-medium text-amber-600 dark:text-amber-400">Update pending</p>
  )
}

export default function Deployments() {
  const navigate = useNavigate()
  const devMode = useDevMode()
  const [deleteConfirm, setDeleteConfirm] = useState<Deployment | null>(null)
  const [redeployConfirm, setRedeployConfirm] = useState<Deployment | null>(null)
  const [assignmentDeployment, setAssignmentDeployment] = useState<Deployment | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  // Bulk remove. Selection is by deployment id, and only rows that are genuinely Intune apps
  // can be selected — a `detected:` row is an app discovered on a device, not something Intune
  // published, so there is nothing there to delete.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkConfirm, setBulkConfirm] = useState(false)
  /** Removing an install app keeps its update-only app instead of deleting it with it. */
  const [keepUpdateOnly, setKeepUpdateOnly] = useState(false)
  const [bulkResults, setBulkResults] = useState<Awaited<ReturnType<typeof bulkRemoveDeployments>> | null>(null)
  const queryClient = useQueryClient()

  // The list is not polled while a removal runs: a poll could bring back rows the removal is
  // deleting, since Intune's app list lags behind the delete, or show a row whose apps are
  // already deleted as Not in Intune before the removal drops its record.
  const removing = useIsMutating({ mutationKey: REMOVE_KEY }) > 0
  const { data, isLoading, isFetching, error, refetch, dataUpdatedAt } = useIntuneQuery({
    queryKey: intuneKeys.deployments(),
    queryFn: listDeployments,
    refetchInterval: removing ? false : 30000,
  })

  // Deployment RUNS, which are a different thing from the deployments below: a run is what happens
  // while a batch is being published. ONLY a run still in flight is shown, and only the most recent
  // one. A finished run — whatever it published — is described by the list below, and a page that
  // stacked finished runs pushed that list off the screen. The same query the header's task list
  // polls, so this page adds no request of its own.
  const jobs = useDeploymentJobsQuery()
  const runningJob = (jobs.data?.deployments ?? []).find(
    (job) => job.status === 'queued' || job.status === 'processing',
  )
  // Dismissal is remembered against the run's id, so it survives a reload and the NEXT deployment
  // still appears — dismissing one run must not silence the feature.
  const [dismissedRun, setDismissedRun] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(DISMISSED_RUN_KEY)
    } catch {
      // Private-browsing and storage-disabled profiles throw on read. The card is worth showing
      // twice; it is not worth a page that fails to render.
      return null
    }
  })
  const showRun = !!runningJob && dismissedRun !== runningJob.id
  const dismissRun = (job: DeploymentJob) => {
    setDismissedRun(job.id)
    try {
      window.localStorage.setItem(DISMISSED_RUN_KEY, job.id)
    } catch {
      /* remembered for this page view only */
    }
  }
  const [retryingJob, setRetryingJob] = useState<string | null>(null)
  const retryJob = useMutation({
    mutationFn: (job: DeploymentJob) => retryDeploymentJob(job.id),
    onMutate: (job: DeploymentJob) => setRetryingJob(job.id),
    onSettled: () => {
      setRetryingJob(null)
      // The list below is re-read when the retried run finishes; see useDeploymentJobsQuery.
      queryClient.invalidateQueries({ queryKey: taskKeys.deploymentJobs() })
    },
  })

  /** What the single Remove removes from a deployment with both apps. */
  const [removeScope, setRemoveScope] = useState<RemoveScope>('deployment')
  const deleteTarget = deleteConfirm
    ? removalTarget(removeScope, deleteConfirm.id,
                    deleteConfirm.role !== 'update_only' ? deleteConfirm.update_only_app_id : null)
    : null
  // What each confirmation is about to delete from Intune: the update-only app and the older
  // versions go with a deployment, so both dialogs list them before Remove can be pressed.
  const deletePreview = useRemovalPreview(
    deleteTarget ? [deleteTarget.id] : [], !!deleteConfirm, deleteTarget?.keepUpdateOnly ?? false)
  const deleteRemoval = deletePreview.preview?.deployments[0]
  /** The selection as deployments of the current list, which is what bulk remove acts on. A row
   *  the search box hides is still selected, so it is listed in the confirmation and removed; an
   *  id the list no longer has is neither listed nor sent. A deployment is selected by the id of
   *  its row's app. */
  const selectedDeployments = groupDeploymentRows((data?.deployments ?? []).filter(d => d.in_catalog))
    .filter(g => selectedIds.has(g.primary.id))
  const selectedRows = selectedDeployments.map(g => g.primary)
  const selectedRowIds = selectedRows.map(d => d.id)
  const bulkPreview = useRemovalPreview(selectedRowIds, bulkConfirm, keepUpdateOnly)
  const bulkHasUpdateOnly = selectedRows.some((d) => d.role !== 'update_only' && !!d.update_only_app_id)
  const bulkRemovals = new Map((bulkPreview.preview?.deployments ?? []).map(d => [d.deployment_id, d]))
  /** Distinct Intune apps the batch deletes: a selected update-only app is also a related app of
   *  its install app and must be counted once. */
  const bulkAppCount = new Set([
    ...selectedRowIds,
    ...(bulkPreview.preview?.deployments ?? []).flatMap(d => d.related.map(r => r.intune_app_id)),
  ]).size

  /** Ids this page has removed. Intune's app list is eventually consistent, so the refetch after
   *  a removal can still return the deleted app for a while; a row the removal reported deleted
   *  is hidden here rather than shown until the next sync. */
  const [removedIds, setRemovedIds] = useState<Set<string>>(() => new Set())
  const hideRemoved = (ids: string[]) =>
    setRemovedIds((prev) => new Set([...prev, ...ids]))
  /** The rows a removal is deleting right now. */
  const [removingIds, setRemovingIds] = useState<Set<string>>(() => new Set())
  const markRemoving = (ids: string[], on: boolean) =>
    setRemovingIds((prev) => {
      const next = new Set(prev)
      ids.forEach((id) => (on ? next.add(id) : next.delete(id)))
      return next
    })

  const deleteMutation = useMutation({
    mutationKey: REMOVE_KEY,
    mutationFn: (target: { id: string; keepUpdateOnly: boolean }) =>
      deleteDeployment(target.id, target.keepUpdateOnly),
    // As in a bulk removal: the row stays marked Removing until the removal answers, and a list
    // read already in flight is dropped. The related apps are captured before the dialog can close.
    onMutate: async (target) => {
      const related = (deleteRemoval?.related ?? []).map(r => r.intune_app_id)
      markRemoving([target.id], true)
      await queryClient.cancelQueries({ queryKey: intuneKeys.deployments() })
      return { related }
    },
    onSettled: (_res, _err, target) => markRemoving([target.id], false),
    onSuccess: (res, _target, ctx) => {
      // 'partial' means the app itself is gone and an older one is not: hide only the row.
      hideRemoved([res.deployment_id, ...(res.status === 'removed' ? ctx?.related ?? [] : [])])
      queryClient.invalidateQueries({ queryKey: intuneKeys.deployments() })
      queryClient.invalidateQueries({ queryKey: ['tenant-apps'] })
      // An older version that could not be deleted is still in Intune; reported in the same
      // place a partly failed batch is.
      if (res.status === 'partial') {
        setBulkResults({
          success: false, requested: 1, removed: 0, failed: 1,
          results: [{ deployment_id: res.deployment_id, status: 'partial',
                      detail: res.detail || res.message, app_name: deleteConfirm?.app_name }],
        })
      }
      setDeleteConfirm(null)
    },
  })

  const relatedIds = (id: string) =>
    (bulkRemovals.get(id)?.related ?? []).map(x => x.intune_app_id)
  const bulkRemoveMutation = useMutation({
    mutationKey: REMOVE_KEY,
    mutationFn: (ids: string[]) => bulkRemoveDeployments(ids, keepUpdateOnly),
    // The selected rows stay on screen marked Removing until the removal answers; then the rows
    // it deleted go and the selection and its counter change in the same render. A list read
    // already in flight is dropped so it cannot bring back a row the removal deleted. The related
    // apps are captured here: the preview they come from is not cached once the dialog closes.
    onMutate: async (ids) => {
      const related: Record<string, string[]> = Object.fromEntries(ids.map(id => [id, relatedIds(id)]))
      markRemoving(ids, true)
      await queryClient.cancelQueries({ queryKey: intuneKeys.deployments() })
      return { related }
    },
    // 'partial' means the app itself is gone and an older one is not, so its row goes and its
    // related apps stay.
    onSettled: (res, _err, ids, ctx) => {
      markRemoving(ids, false)
      if (!ctx || !res) return
      hideRemoved(res.results.flatMap(r =>
        r.status === 'removed' ? [r.deployment_id, ...(ctx.related[r.deployment_id] ?? [])]
          : r.status === 'partial' ? [r.deployment_id] : []))
      setSelectedIds(new Set(
        res.results.filter(r => r.status !== 'removed' && r.status !== 'partial').map(r => r.deployment_id)))
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: intuneKeys.deployments() })
      queryClient.invalidateQueries({ queryKey: ['tenant-apps'] })
      setBulkConfirm(false)
      // Kept on screen even when everything succeeded: a batch that partly failed must not be
      // mistaken for a clean one, and the only place that distinction exists is per id.
      setBulkResults(res)
    },
  })

  const redeployMutation = useMutation({
    mutationFn: (deploymentId: string) => redeployToIntune(deploymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: intuneKeys.deployments() })
      queryClient.invalidateQueries({ queryKey: ['deployment-details', redeployConfirm?.id] })
      setRedeployConfirm(null)
    },
  })

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'N/A'
    return new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z').toLocaleString()
  }

  /** Only a real Intune deployment can be bulk-removed. A `detected:` id is a device discovery,
   *  not an Intune app, and the API refuses anything that is not an Intune app id (a GUID). */
  const isRemovable = (d: Deployment) =>
    d.target_type === 'intune' && d.status !== 'detected' && !d.id.startsWith('detected:')

  const toggleSelected = (id: string) => {
    setBulkResults(null)
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  // Only a failure with nothing cached takes the page; a failed refresh over existing rows keeps
  // them on screen and reports itself in a banner instead.
  if (error && !data) {
    return (
      <div className="p-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-6">
          <h2 className="text-red-700 dark:text-red-400 font-medium mb-2">Failed to load deployments</h2>
          <p className="text-theme-text-secondary text-sm">{(error as Error).message}</p>
          <button
            onClick={() => refetch()}
            className="mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm"
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  // Only deployments that came from the catalog. Apps deployed directly to Intune, and apps
  // merely DETECTED on an endpoint, belong to Discovery instead.
  //
  // This filter is easy to mistake for a broken page: against the live tenant the endpoint
  // returns 169 rows and every one is status='detected' / in_catalog=false, so all 169 are
  // dropped here and the table renders empty. That is correct, but "No active deployments" on
  // its own reads as a fault. The counts below let the empty state say what was filtered and
  // where it went.
  const allRows = data?.deployments || []
  const catalogApps = allRows.filter(d => d.in_catalog && !removedIds.has(d.id))
  const detectedHidden = allRows.length - catalogApps.length
  // One row per deployment: its install app and its update-only app together.
  const catalogRows = groupDeploymentRows(catalogApps)
  const deployments = catalogRows.filter(({ primary: d }) => {
    if (!searchQuery) return true
    const q = searchQuery.toLowerCase()
    return [packageName(d), d.app_name, d.publisher, d.version, d.deployment_name || 'Default',
            uxTemplateLabel(d), deliveryTemplateLabel(d)]
      .some((v) => (v || '').toLowerCase().includes(q))
  })
  // The rows select-all may touch: removable AND currently visible under the search filter.
  const removableVisible = deployments.map(g => g.primary).filter(isRemovable)
  // Whether Intune is connected is UNKNOWN until the first read lands, and unknown is not
  // unconfigured: `intune_configured` says the tenant has stored Intune credentials, and reading
  // it off an undefined response tells a configured tenant to go and configure Intune for the
  // several seconds the Graph call takes.
  const intuneKnown = data !== undefined
  const intuneConfigured = data?.intune_configured
  const intuneError = data?.intune_error

  return (
    <div className="p-8">
      {/* Header — the shared primitive, so this page agrees with every other on spacing and
          weight rather than hand-rolling its own h1 (mock: PageHeader). */}
      <PageHeader
        title="Intune Deployment"
        subtitle="Manage app deployments across targets"
        actions={
          <div className="flex items-center gap-3">
            <SyncStatus
              syncing={isFetching}
              updatedAt={dataUpdatedAt}
              label="Syncing with Microsoft Intune…"
            />
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="flex items-center gap-2 rounded-lg border border-theme-border-primary px-3 py-2 text-sm text-theme-text-primary hover:bg-theme-tertiary disabled:opacity-50"
            >
              <ArrowPathIcon className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        }
      />

      <div className="mb-5">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search by package, deployment, template or version…"
          className="w-full max-w-md"
        />
      </div>

      {/* The deployment being published RIGHT NOW: open, so its per-item progress is readable, and
          dismissable. Nothing is shown once it lands — what it published is then in the list
          below. */}
      {showRun && runningJob && (
        <div className="mb-6">
          <DeploymentJobsPanel
            jobs={[runningJob]}
            expandedByDefault={() => true}
            onDismiss={dismissRun}
            onRetry={(job) => retryJob.mutate(job)}
            retryingId={retryingJob}
          />
        </div>
      )}

      {error && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
          <div>
            <p className="text-sm font-semibold text-red-600 dark:text-red-300">Could not refresh from Intune</p>
            <p className="text-sm text-fmuted">
              Showing the deployments from the last successful read. {(error as Error).message}
            </p>
          </div>
        </div>
      )}

      {/* Intune Status Banner. NOT SHOWN WHILE THE ANSWER IS UNKNOWN, and unknown covers two
          states: the first read has not landed, and the read that landed could not tell — the
          backend reports `intune_configured: false` when the configuration lookup itself threw, and
          "go and configure Intune" sends a configured tenant nowhere. The failure is named by the
          banner below instead. */}
      {intuneKnown && !intuneConfigured && !intuneError && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-amber-500/25 bg-amber-500/10 px-4 py-3">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-amber-500 dark:text-amber-400" />
          <div>
            <p className="text-sm font-semibold text-amber-600 dark:text-amber-300">Intune not configured</p>
            <p className="text-sm text-fmuted">
              Configure Microsoft Intune integration in{' '}
              <Link to="/settings" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
                Settings
              </Link>{' '}
              to see live deployment status.
            </p>
          </div>
        </div>
      )}

      {intuneError && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500 dark:text-red-400" />
          <div>
            <p className="text-sm font-semibold text-red-600 dark:text-red-300">Failed to connect to Intune</p>
            <p className="text-sm text-fmuted">{intuneError}</p>
          </div>
        </div>
      )}

      {intuneConfigured && !intuneError && (
        <div className="mb-5 flex items-start gap-3 rounded-xl border border-emerald-500/25 bg-emerald-500/10 px-4 py-3">
          <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500 dark:text-emerald-400" />
          <div>
            <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-300">Connected to Microsoft Intune</p>
            <p className="text-sm text-fmuted">
              Showing live deployment status from Intune combined with local catalog data.
            </p>
          </div>
        </div>
      )}

      {/* What the rows above are read from. The filter is client-side because Graph's isof()
          filter on mobileApps is not consistently supported. Gated on dev mode here as well as
          inside the note, so the spacing does not survive the note it belongs to. */}
      {devMode && (
        <div className="mb-5">
          <DevNote
            setting="GET /deviceAppManagement/mobileApps?$expand=assignments"
            example="Assigned is assignments.length > 0. Auto-follow and the update-only twin are ours — Intune tracks neither."
          />
        </div>
      )}

      {/* Bulk action bar. Only rendered with a selection, so the destructive control is not
          sitting on screen during ordinary browsing — the same reasoning that keeps the
          single-row Remove behind the overflow menu. */}
      {selectedRows.length > 0 && (
        <div className="mb-3 flex items-center justify-between rounded-lg border border-theme-border-primary bg-theme-tertiary px-4 py-2.5">
          <span className="text-sm text-theme-text-primary">
            <span className="font-semibold">{selectedRows.length}</span> deployment{selectedRows.length === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setSelectedIds(new Set()); setBulkResults(null) }}
              className="rounded-lg border border-theme-border-primary px-3 py-1.5 text-[13px] text-theme-text-primary hover:bg-theme-secondary"
            >
              Clear
            </button>
            <button
              onClick={() => {
                setKeepUpdateOnly(false)
                setBulkConfirm(true)
              }}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-red-700"
            >
              Remove selected
            </button>
          </div>
        </div>
      )}

      {/* Per-id outcome of the last bulk remove. Shown for successes too: "12 requested, 9
          removed" is the only place a partly-failed batch is visible. */}
      {bulkResults && (
        <div className="mb-3 rounded-lg border border-theme-border-primary bg-theme-card px-4 py-3">
          <p className="text-sm text-theme-text-primary">
            {bulkResults.removed} of {bulkResults.requested} removed
            {bulkResults.failed > 0 && <span className="text-red-600 dark:text-red-400"> — {bulkResults.failed} failed</span>}
          </p>
          {bulkResults.failed > 0 && (
            <ul className="mt-2 space-y-1">
              {bulkResults.results.filter(r => r.status !== 'removed').map(r => (
                <li key={r.deployment_id} className="text-xs text-theme-text-secondary">
                  <span className="font-mono">{r.app_name || r.deployment_id}</span>
                  {' — '}{r.status}{r.detail ? `: ${r.detail}` : ''}
                </li>
              ))}
            </ul>
          )}
          <button
            onClick={() => setBulkResults(null)}
            className="mt-2 text-xs text-theme-text-tertiary hover:text-theme-text-primary underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Deployments table. The count belongs to the card's own footer, as the reference has it —
          a loose line under the card reads as page text rather than as the table's total. */}
      <TableCard
        footer={
          <span>
            Showing {deployments.length} deployment{deployments.length !== 1 ? 's' : ''}
          </span>
        }
      >
        <thead>
          <tr className="border-b border-theme-border-primary">
            <th className="th w-10">
              {/* Selects the removable rows CURRENTLY VISIBLE (search-filtered), never rows
                  the user cannot see — a select-all that reaches past the filter is how a
                  bulk delete hits something unintended. */}
              <input
                type="checkbox"
                aria-label="Select all removable deployments"
                className="h-4 w-4 rounded border-theme-border-primary"
                disabled={removableVisible.length === 0}
                checked={removableVisible.length > 0 && removableVisible.every(d => selectedIds.has(d.id))}
                onChange={(e) => {
                  setBulkResults(null)
                  setSelectedIds(e.target.checked ? new Set(removableVisible.map(d => d.id)) : new Set())
                }}
              />
            </th>
            <th className="th whitespace-nowrap">Package</th>
            <th className="th whitespace-nowrap">Deployment</th>
            <th className="th whitespace-nowrap">Apps</th>
            <th className="th whitespace-nowrap">Version</th>
            <th className="th whitespace-nowrap">Context</th>
            <th className="th whitespace-nowrap">Templates</th>
            {/* Whether and when a newer version publishes on its own, from the deployment's own
                update policy. */}
            <th className="th whitespace-nowrap">Updates</th>
            <th className="th whitespace-nowrap">Status</th>
            <th className="th w-10">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-theme-border-primary">
          {isLoading ? (
            <TableSkeleton cols={10} />
          ) : deployments.length === 0 ? (
            <tr>
              <td colSpan={10}>
                {searchQuery && catalogRows.length > 0 ? (
                  <EmptyState
                    icon={<Squares2X2Icon className="h-7 w-7" />}
                    title="No deployments match that search"
                    hint={`${catalogRows.length} deployment${catalogRows.length === 1 ? '' : 's'} hidden by the current filter.`}
                  />
                ) : detectedHidden > 0 ? (
                  // The common case, and the one that used to look like a bug: nothing has been
                  // published from the catalog, but the tenant is far from empty.
                  <EmptyState
                    icon={<Squares2X2Icon className="h-7 w-7" />}
                    title="No deployments from the catalog yet"
                    hint={`${detectedHidden} app${detectedHidden === 1 ? '' : 's'} detected on your endpoints are not shown here — apps that were not deployed through the platform live under Discovery. Deploy an app from the catalog to see it on this page.`}
                    action={
                      <Link
                        to="/discovery"
                        className="rounded-lg border border-theme-border-primary px-3 py-1.5 text-sm text-theme-text-primary hover:bg-theme-tertiary"
                      >
                        Go to Discovery
                      </Link>
                    }
                  />
                ) : (
                  <EmptyState
                    icon={<Squares2X2Icon className="h-7 w-7" />}
                    title="No active deployments"
                    hint="Deploy an app from the catalog to see it here."
                  />
                )}
              </td>
            </tr>
          ) : (
            deployments.map((group) => {
              const deployment = group.primary
              return (
              <tr
                key={group.key}
                className={`row-hover cursor-pointer ${removingIds.has(deployment.id) ? 'opacity-50' : ''}`}
                // The row opens the deployment page; its checkbox, links and buttons do their own thing.
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('input, button, a, [role="menu"]')) return
                  navigate(`/deployments/${deployment.id}`)
                }}
              >
                <td className="td w-10" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    aria-label={`Select ${deployment.app_name}`}
                    className="h-4 w-4 rounded border-theme-border-primary disabled:opacity-40"
                    // A detected/non-Intune row has nothing for Intune to delete, and the API
                    // rejects a non-GUID id, so it cannot be selected in the first place.
                    disabled={!isRemovable(deployment) || removingIds.has(deployment.id)}
                    title={isRemovable(deployment) ? undefined : 'Not an Intune deployment — nothing to remove'}
                    checked={selectedIds.has(deployment.id)}
                    onChange={() => toggleSelected(deployment.id)}
                  />
                </td>
                <td className="td">
                  <div className="flex items-center gap-3 whitespace-nowrap">
                    {deployment.icon_url ? (
                      <img
                        src={deployment.icon_url}
                        alt=""
                        className="h-[34px] w-[34px] rounded-lg object-contain bg-theme-tertiary"
                        onError={(e) => {
                          // Fall back to letter avatar on error
                          const target = e.target as HTMLImageElement
                          target.style.display = 'none'
                          target.nextElementSibling?.classList.remove('hidden')
                        }}
                      />
                    ) : null}
                    <div className={`flex h-[34px] w-[34px] items-center justify-center rounded-lg bg-theme-tertiary ${deployment.icon_url ? 'hidden' : ''}`}>
                      <span className="font-medium text-ftext">{packageName(deployment).charAt(0) || '?'}</span>
                    </div>
                    <div className="min-w-0">
                      <Link
                        to={`/deployments/${deployment.id}`}
                        title={packageName(deployment)}
                        className="block max-w-[220px] truncate font-medium text-ftext hover:underline"
                      >
                        {packageName(deployment)}
                      </Link>
                      <p className="max-w-[220px] truncate text-[12px] text-fmuted">
                        {deployment.publisher || '—'}
                        {!deployment.synced && (
                          <span className="ml-1 text-yellow-600 dark:text-yellow-400">(offline)</span>
                        )}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="td">
                  {/* The package's first deployment has no name; it is the Default one. */}
                  <p
                    title={deployment.deployment_name || 'Default'}
                    className="max-w-[160px] truncate font-semibold text-ftext"
                  >
                    {deployment.deployment_name || 'Default'}
                  </p>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                    {removingIds.has(deployment.id) && <Badge tone="slate">Removing…</Badge>}
                    {/* A deployment of a catalog entry that is not approved: it can be updated or
                        removed, and new deployments go to the package's current entry. */}
                    {deployment.catalog_status &&
                      deployment.catalog_status !== 'approved' &&
                      (deployment.catalog_replaced_by &&
                      leftTheCatalog(deployment.catalog_status) ? (
                        <Link
                          to={`/apps/${deployment.catalog_replaced_by.id}`}
                          title={`Replaced by ${deployment.catalog_replaced_by.name} ${deployment.catalog_replaced_by.version}`}
                        >
                          <Badge tone="amber">Catalog entry replaced</Badge>
                        </Link>
                      ) : (
                        <span
                          title={`This catalog entry ${notInCatalogPhrase(deployment.catalog_status)}. The deployment can still be updated or removed.`}
                        >
                          <Badge tone="slate">{notInCatalogLabel(deployment.catalog_status)}</Badge>
                        </span>
                      ))}
                    {group.apps.some((a) => a.removal_incomplete) && (
                      <span title="Removing this deployment deleted its newer apps from Intune but not this one. It is not updated automatically. Remove it to finish.">
                        <Badge tone="red">Removal incomplete</Badge>
                      </span>
                    )}
                    {group.apps.some((a) => a.failed_publish) && (
                      <span title="A publish failed after Intune created its app. That app has no assignments and is left in Intune. Open the package and remove it.">
                        <Badge tone="red">Failed publish left in Intune</Badge>
                      </span>
                    )}
                  </div>
                </td>
                <td className="td">
                  {/* The apps the deployment has: the install app, and the update-only app that
                      updates devices which already have the app. */}
                  <div className="flex flex-wrap items-center gap-1.5">
                    {group.install && (
                      <span
                        title={group.install.app_name || 'The install app: installs the app on the devices it is assigned to.'}
                        className="rounded-full bg-blue-500/15 px-2.5 py-0.5 text-[12px] font-medium text-blue-600 dark:text-blue-300"
                      >
                        Install
                      </span>
                    )}
                    {group.update && (
                      <span
                        title={group.update.app_name || 'The update-only app: updates the app only on devices that already have it.'}
                        className="rounded-full bg-violet-500/15 px-2.5 py-0.5 text-[12px] font-medium text-violet-600 dark:text-violet-300"
                      >
                        Update
                      </span>
                    )}
                  </div>
                </td>
                <td className="td whitespace-nowrap tabular-nums">{deployment.version || 'N/A'}</td>
                <td className="td">
                  {deployment.install_context === 'user'
                    ? 'User'
                    : deployment.install_context === 'system'
                      ? 'System'
                      : '—'}
                </td>
                <td
                  className="td"
                  title={`User experience: ${uxTemplateLabel(deployment)}\nDeployment template: ${deliveryTemplateLabel(deployment)}`}
                >
                  {/* The user experience template, then the deployment template. */}
                  <p className="max-w-[200px] truncate text-[13px] text-ftext">
                    {uxTemplateLabel(deployment)}
                  </p>
                  <p className="max-w-[200px] truncate text-[12px] text-fmuted">
                    {deliveryTemplateLabel(deployment)}
                  </p>
                </td>
                <td className="td whitespace-nowrap">
                  {deployment.update_mode === 'PIN' ? (
                    // Pinned is neither following nor manual: it is deliberately held on a
                    // version, and saying which one is the whole point of pinning.
                    <span className="text-[12px] text-fmuted">
                      Pinned{deployment.pinned_version ? ` to v${deployment.pinned_version}` : ''}
                    </span>
                  ) : deployment.update_mode === 'FOLLOW' &&
                    deployment.follow_new_versions !== false ? (
                    /* The soak is the other half of the answer and used to be missing here: an
                       app whose template holds a new version for three days read "Immediately",
                       which is indistinguishable from a soak that is not running. */
                    <span className="inline-flex items-center gap-1.5 text-[12px] text-emerald-600 dark:text-emerald-300">
                      <ArrowPathIcon className="h-3.5 w-3.5" />
                      {deployment.soak_days ? `After ${plural(deployment.soak_days, 'day')}` : 'Immediately'}
                    </span>
                  ) : (
                    // Either the subscription or the template says a new version waits for a
                    // manual publish; both leave the admin doing the same thing.
                    <span className="text-[12px] text-ffaint">Manual</span>
                  )}
                  <PendingUpdate deployment={deployment} />
                </td>
                <td className="td">
                  {/* The worst status of the deployment's apps. Assigned means every app carries
                      an assignment, not that it published: an app with none is in Intune and
                      installs on nothing. */}
                  <DeploymentStatusBadge status={group.status} fallback={deployment.status} />
                  {group.updateMissing && (
                    <p
                      className="mt-1 text-[12px] text-red-500"
                      title="The update app of this deployment was deleted in the Intune admin center. Open the deployment to recreate it or remove it from the deployment."
                    >
                      Update app missing in Intune
                    </p>
                  )}
                </td>
                <td className="td" onClick={(e) => e.stopPropagation()}>
                  {/* Two tiers, as the mock has them: the actions you reach for stay visible,
                      and the destructive one goes behind the overflow so a mis-click on a busy
                      row cannot delete a deployment. */}
                  <div className="flex items-center justify-end gap-1.5">
                    {/* Assign and Redeploy act on the app object in Intune; with none there they
                        can only fail. Remove stays: it drops the record. */}
                    <Menu
                      trigger={
                        <button
                          aria-label="More actions"
                          className="rounded-md p-1.5 text-fmuted hover:bg-theme-tertiary hover:text-ftext"
                        >
                          <EllipsisHorizontalIcon className="h-4 w-4" />
                        </button>
                      }
                    >
                      {(close) => (
                        <>
                          {/* Assign and Redeploy act on the app object in Intune; with none
                              there they can only fail. Remove stays: it drops the record. */}
                          {deployment.in_intune !== false && (
                            <MenuItem
                              onClick={() => {
                                setAssignmentDeployment(deployment)
                                close()
                              }}
                            >
                              Assign
                            </MenuItem>
                          )}
                          {deployment.in_catalog && deployment.app_id && (
                            <MenuItem
                              onClick={() => {
                                navigate(`/apps/${deployment.app_id}`)
                                close()
                              }}
                            >
                              View in catalog
                            </MenuItem>
                          )}
                          {/* The mock offers "Sync now"; we have no per-deployment sync
                              endpoint, and redeploy is the real action we do have. */}
                          {deployment.in_intune !== false && (
                            <MenuItem
                              onClick={() => {
                                setRedeployConfirm(deployment)
                                close()
                              }}
                            >
                              Redeploy
                            </MenuItem>
                          )}
                          <MenuItem
                            danger
                            onClick={() => {
                              setRemoveScope('deployment')
                              setDeleteConfirm(deployment)
                              close()
                            }}
                          >
                            Remove deployment
                          </MenuItem>
                        </>
                      )}
                    </Menu>
                  </div>
                </td>
              </tr>
              )
            })
          )}
        </tbody>
      </TableCard>

      {/* Bulk Remove Confirmation. Names every deployment rather than just a count: the count
          alone does not let you notice that the selection caught something you did not mean. */}
      {bulkConfirm && (
        <div className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setBulkConfirm(false)} />
          <div className="relative bg-theme-card rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 border border-theme-border-primary">
            <h3 className="text-lg font-bold text-theme-text-primary mb-4">
              Remove {selectedRows.length} deployment{selectedRows.length === 1 ? '' : 's'}
            </h3>
            <p className="text-theme-text-secondary mb-3">
              These apps and their assignments will be deleted from Microsoft Intune, each with
              its update-only app and older versions. Nothing is uninstalled from devices. The
              apps remain in your local catalog.
            </p>
            <ul className="mb-4 max-h-48 overflow-y-auto rounded border border-theme-border-primary divide-y divide-theme-border-primary">
              {selectedRows.map(d => {
                const removal = bulkRemovals.get(d.id)
                const also = relatedSummary(removal?.related ?? [])
                return (
                  <li key={d.id} className="px-3 py-2 text-sm text-theme-text-primary">
                    <span className="font-medium">{deploymentTitle(packageName(d), d.deployment_name)}</span>
                    {d.version && <span className="text-theme-text-tertiary"> · {d.version}</span>}
                    {/* The Intune apps are details of the deployment. */}
                    <span className="block text-xs text-theme-text-tertiary">
                      {d.app_name}{also ? `; ${also}` : ''}
                    </span>
                    {removal && <RelationshipNotes item={removal} />}
                  </li>
                )
              })}
            </ul>
            <KeepUpdateOnlyOption shown={bulkHasUpdateOnly} keep={keepUpdateOnly} onChange={setKeepUpdateOnly} />
            <div className="mb-4">
              <GroupsNote items={[...bulkRemovals.values()]} />
              <RemovalPreviewStatus query={bulkPreview.query} />
            </div>
            {bulkRemoveMutation.error && (
              <div className="mb-4 rounded border border-red-300 bg-red-50 dark:bg-red-900/20 p-3">
                <p className="text-red-700 dark:text-red-400 text-sm">{errorText(bulkRemoveMutation.error, 'Remove failed.')}</p>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setBulkConfirm(false)}
                disabled={bulkRemoveMutation.isPending}
                className="px-4 py-2 rounded-lg border border-theme-border-primary text-theme-text-primary hover:bg-theme-tertiary disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => bulkRemoveMutation.mutate(selectedRowIds)}
                disabled={bulkRemoveMutation.isPending || !bulkPreview.preview || removalBlocked(bulkPreview.preview.deployments)}
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {bulkRemoveMutation.isPending
                  ? `Removing ${selectedRows.length}...`
                  : bulkPreview.preview
                    ? `Remove ${selectedRows.length} (${bulkAppCount} Intune app${bulkAppCount === 1 ? '' : 's'})`
                    : `Remove ${selectedRows.length}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setDeleteConfirm(null)} />
          <div className="relative bg-theme-card rounded-lg shadow-xl max-w-md w-full mx-4 p-6 border border-theme-border-primary">
            <h3 className="text-lg font-bold text-theme-text-primary mb-4">Remove Deployment</h3>
            <p className="text-theme-text-secondary mb-2">
              Are you sure you want to remove <span className="font-semibold text-theme-text-primary">{deploymentTitle(packageName(deleteConfirm), deleteConfirm.deployment_name)}</span> from {targetTypeLabels[deleteConfirm.target_type]}?
            </p>
            <RemoveScopeChoice
              shown={deleteConfirm.role !== 'update_only' && !!deleteConfirm.update_only_app_id}
              scope={removeScope}
              onChange={setRemoveScope}
              disabled={deleteMutation.isPending}
            />
            {deleteRemoval?.role === 'update_only' ? (
              <div className="text-theme-text-tertiary text-sm mb-4">
                <p>
                  Only the update-only app is deleted: devices that already have the app are no
                  longer updated by Intune. The install app and its assignments are not changed.
                  {deleteRemoval.related.length > 0 ? ' Deleted with it:' : ''}
                </p>
                <RelatedAppList related={deleteRemoval.related} />
                <RelationshipNotes item={deleteRemoval} />
                <GroupsNote items={[deleteRemoval]} />
              </div>
            ) : deleteRemoval ? (
              <div className="text-theme-text-tertiary text-sm mb-4">
                <p>
                  The app and its assignments are deleted from Microsoft Intune
                  {deleteRemoval.related.length > 0 ? ', together with:' : '.'}
                </p>
                <RelatedAppList related={deleteRemoval.related} />
                <RelationshipNotes item={deleteRemoval} />
                <p>
                  Nothing is uninstalled from devices, but Intune will no longer install, update
                  or uninstall this app. The app will remain in your local catalog if it exists
                  there.
                </p>
                <GroupsNote items={[deleteRemoval]} />
              </div>
            ) : null}
            <div className="mb-6">
              <RemovalPreviewStatus query={deletePreview.query} />
            </div>

            {deleteMutation.error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                <p className="text-red-700 dark:text-red-400 text-sm">{errorText(deleteMutation.error, 'Remove failed.')}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 bg-theme-tertiary hover:bg-theme-secondary text-theme-text-primary rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
                disabled={deleteMutation.isPending || !deleteRemoval || removalBlocked(deletePreview.preview?.deployments)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {deleteMutation.isPending ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Removing...
                  </>
                ) : (
                  <>
                    <TrashIcon className="w-4 h-4" />
                    Remove
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Redeploy Confirmation Modal */}
      {redeployConfirm && (
        <div className="fixed inset-0 z-50 overflow-hidden flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => !redeployMutation.isPending && setRedeployConfirm(null)} />
          <div className="relative bg-theme-card rounded-lg shadow-xl max-w-md w-full mx-4 p-6 border border-theme-border-primary">
            <h3 className="text-lg font-bold text-theme-text-primary mb-4">Redeploy to Intune</h3>
            <p className="text-theme-text-secondary mb-2">
              Redeploy <span className="font-semibold text-theme-text-primary">{redeployConfirm.app_name}</span> metadata to Intune?
            </p>
            <p className="text-theme-text-tertiary text-sm mb-6">
              This will update the app's setupFilePath, install, and uninstall commands in Intune without re-uploading the package. Assignments will be preserved.
            </p>

            {redeployMutation.isSuccess && (
              <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
                <p className="text-green-700 dark:text-green-400 text-sm">Successfully redeployed to Intune!</p>
              </div>
            )}

            {redeployMutation.error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
                <p className="text-red-700 dark:text-red-400 text-sm">
                  {errorText(redeployMutation.error)}
                </p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <button
                onClick={() => { setRedeployConfirm(null); redeployMutation.reset() }}
                disabled={redeployMutation.isPending}
                className="px-4 py-2 bg-theme-tertiary hover:bg-theme-secondary text-theme-text-primary rounded-lg transition-colors disabled:opacity-50"
              >
                {redeployMutation.isSuccess ? 'Close' : 'Cancel'}
              </button>
              {!redeployMutation.isSuccess && (
                <button
                  onClick={() => redeployMutation.mutate(redeployConfirm.id)}
                  disabled={redeployMutation.isPending}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  {redeployMutation.isPending ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                      </svg>
                      Redeploying...
                    </>
                  ) : (
                    <>
                      <ArrowPathIcon className="w-4 h-4" />
                      Redeploy
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {assignmentDeployment && (
        /* An update-only row IS the twin, so it opens on the twin's own object — Required only,
           and titled as such. An install row edits both objects side by side, because assigning
           one assigns nothing on the other. */
        <AssignmentModal
          isOpen={true}
          onClose={() => setAssignmentDeployment(null)}
          deploymentId={
            (assignmentDeployment.role === 'update_only'
              ? assignmentDeployment.parent_intune_app_id ?? assignmentDeployment.intune_app_id
              : assignmentDeployment.intune_app_id) || ''
          }
          appName={assignmentDeployment.app_name || 'Unknown App'}
          updateOnlyAppId={
            (assignmentDeployment.role === 'update_only'
              ? assignmentDeployment.intune_app_id
              : assignmentDeployment.update_only_app_id) || undefined
          }
          scope={assignmentDeployment.role === 'update_only' ? 'updateOnly' : 'both'}
          onSuccess={() => queryClient.invalidateQueries({ queryKey: intuneKeys.deployments() })}
        />
      )}

    </div>
  )
}
